import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchComponent from "../../components/search";
import Table from "../../components/datatable/datatable";
import { useNavigate } from "react-router-dom";
import {
  getAllUserInvoices,
  customerFetchPartnerInvoices,
  customerDownloadPartnerInvoicePdf,
} from "../../core/axios/endpoints";
import { useUser } from "../../core/context/user";
import { RoutesFullPath } from "../../utils/constants";
import { hasAnyPartnerRole } from "../../core/partner-subsidie";

const fmtEUR = (n) =>
  Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");

// Jira-lozenges: waarschuwing / gevaar / succes / neutraal (designbrief).
const PARTNER_STATUS_LABELS = {
  sent: { label: "Open", color: "bg-[#FFF7D6] text-[#7F5F01]" },
  overdue: { label: "Achterstallig", color: "bg-[#FFECEB] text-[#AE2A19]" },
  paid: { label: "Betaald", color: "bg-[#DCFFF1] text-[#1F845A]" },
  cancelled: { label: "Geannuleerd", color: "bg-[#091E420F] text-ink-subtle" },
};

const LOZENGE = "inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide";

const ClientFacturenPage = () => {
  const { userData } = useUser();
  // Partner-facturen kunnen BTW- én subsidie-regels bevatten, dus de tab
  // verschijnt voor elke partner-rol.
  const isBtwPartner = hasAnyPartnerRole(userData);
  const navigate = useNavigate();

  // Tab: 'subsidie' (default) of 'partner'. Alleen zichtbaar voor partners.
  const [tab, setTab] = useState("subsidie");
  const [data, setData] = useState({ data: [], totalDataCount: 0 });
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const subsidieColumns = useMemo(
    () => [
      {
        title: "Factuur Nummer",
        dataIndex: "InvoiceId",
        render: (text) => <span className="font-medium text-ink">{text}</span>,
      },
      {
        title: "Regeling",
        dataIndex: "application",
        render: (application) => <span>{application?.regling ?? "-"}</span>,
      },
      {
        title: "Betaald Op",
        dataIndex: "betaaldDateTime",
        render: (text) => <span>{fmtDate(text)}</span>,
      },
      {
        title: "Totaal",
        dataIndex: "application",
        render: (application) => <span className="tabular-nums">&euro;{fmtEUR(application?.totaal)}</span>,
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (text) => (
          <span
            className={`${LOZENGE} ${
              text === "Betaald" ? "bg-[#DCFFF1] text-[#1F845A]" : "bg-[#FFF7D6] text-[#7F5F01]"
            }`}
          >
            {text}
          </span>
        ),
      },
    ],
    [],
  );

  const partnerColumns = useMemo(
    () => [
      {
        title: "Factuurnummer",
        dataIndex: "invoiceNumber",
        render: (text) => <span className="text-sm font-medium text-ink">{text}</span>,
      },
      {
        title: "Type",
        dataIndex: "type",
        render: (v) => (
          <span
            className={`${LOZENGE} ${
              v === "credit_note"
                ? "bg-[#FFECEB] text-[#AE2A19]"
                : "bg-[#091E420F] text-ink-subtle"
            }`}
          >
            {v === "credit_note" ? "Creditnota" : "Factuur"}
          </span>
        ),
      },
      {
        title: "Datum",
        dataIndex: "invoiceDate",
        render: (v) => <span>{fmtDate(v)}</span>,
      },
      {
        title: "Kenmerk",
        dataIndex: "kenmerk",
        render: (v) => <span className="text-sm text-ink-subtle">{v}</span>,
      },
      {
        title: "Totaal (incl. btw)",
        dataIndex: "totalIncl",
        render: (v) => (
          <span className="font-medium tabular-nums">&euro;{fmtEUR(v)}</span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (v) => {
          const cfg =
            PARTNER_STATUS_LABELS[v] ?? PARTNER_STATUS_LABELS.sent;
          return (
            <span
              className={`${LOZENGE} ${cfg.color}`}
            >
              {cfg.label}
            </span>
          );
        },
      },
      {
        title: "PDF",
        dataIndex: "_id",
        render: (id) => (
          <button
            type="button"
            onClick={async (e) => {
              e.stopPropagation();
              if (downloadingId) return;
              setDownloadingId(id);
              try {
                const blob = await customerDownloadPartnerInvoicePdf(id);
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `factuur-${id}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                console.error("PDF download mislukt:", err);
              } finally {
                setDownloadingId(null);
              }
            }}
            className="inline-flex h-7 items-center rounded bg-[#091E420F] px-2.5 text-xs font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
            disabled={downloadingId === id}
          >
            {downloadingId === id ? "Bezig..." : "Download"}
          </button>
        ),
      },
    ],
    [downloadingId],
  );

  const fetchData = useCallback(
    async ({ page, pageSize, resetPageSize }, searchText = "") => {
      setLoading(true);
      try {
        if (tab === "partner") {
          const body = await customerFetchPartnerInvoices({
            page,
            limit: pageSize,
          });
          setData({
            data: (body?.docs ?? []).map((item, i) => ({ ...item, id: i })),
            totalDataCount: body?.pagination?.totalDocs ?? 0,
            resetPageSize,
          });
        } else {
          const body = await getAllUserInvoices(page, pageSize, searchText);
          setData({
            data: (body?.docs ?? []).map((item, i) => ({ ...item, id: i })),
            totalDataCount: body?.totalDocs ?? 0,
            resetPageSize,
          });
        }
      } catch (e) {
        console.error("Facturen ophalen mislukt:", e);
      } finally {
        setLoading(false);
      }
    },
    [tab],
  );

  useEffect(() => {
    fetchData({ page: 1, pageSize: 10, resetPageSize: true });
  }, [tab]);

  const handleRowClick = (record) => {
    if (tab === "subsidie" && record?._id) {
      navigate(`${RoutesFullPath.BetalingDetails}/${record?._id}`);
    }
    // partner-facturen: detail-view komt later, voor nu doet rij-klik niets.
  };

  const handleSearch = (query) => {
    if (tab !== "subsidie") return; // partner-list heeft geen search in V1
    fetchData({ page: 1, pageSize: 10, resetPageSize: true }, query);
  };

  const tabClass = (active) =>
    `-mb-px px-3 py-2 text-sm font-medium ${
      active ? "border-b-2 border-brand text-brand" : "text-ink-subtle hover:text-brand"
    }`;

  return (
    <div className="page-wrapper">
      <div className="content">
        <header className="mb-6">
          <h1 className="text-2xl font-medium text-ink">Facturen</h1>
          <p className="mt-1 text-sm text-ink-subtlest">
            Overzicht van uw facturen. Klik op een rij voor de betaaldetails.
          </p>
        </header>
        <div className="mb-4 space-y-4">
          {isBtwPartner ? (
            <div className="flex gap-2 border-b border-line">
              <button
                type="button"
                onClick={() => setTab("subsidie")}
                className={tabClass(tab === "subsidie")}
              >
                Subsidie-facturen
              </button>
              <button
                type="button"
                onClick={() => setTab("partner")}
                className={tabClass(tab === "partner")}
              >
                Partner-facturen
              </button>
            </div>
          ) : null}

          {tab === "subsidie" ? (
            <div className="max-w-md">
              <SearchComponent onSearch={handleSearch} type={"invoices"} />
            </div>
          ) : null}
        </div>
        <div className="table-responsive rounded bg-white shadow-card">
          <Table
            columns={tab === "subsidie" ? subsidieColumns : partnerColumns}
            response={data}
            fetchData={fetchData}
            onRowClick={handleRowClick}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientFacturenPage;
